import {
  describeDisagreement,
  type LanguageDisagreement,
  type TrackDisagreement,
} from "./tracks";

/**
 * Where a catalogue/manifest language disagreement GOES (TV-5).
 *
 * This file exists because the alternative is a silent kill switch, and this
 * codebase has three "report it, do not swallow it" comments precisely because
 * that pattern has already cost it: the signage surface's four consecutive
 * silent "recoveries", the DRM path's `undefined`-and-play-in-the-clear, and
 * `playbackTelemetry`'s per-code failure map. A player that quietly renders the
 * shorter of two disagreeing language lists is the same shape of mistake — the
 * guest gets a defensible experience and the catalogue error is invisible
 * forever.
 *
 * ## Two outlets, because the two audiences are different
 *
 * 1. **A named warning**, once per title per code. It lands in the console AND
 *    in the in-app log ring (`src/tv/lib/logBuffer.ts`, 500 lines), which the
 *    remote `log_capture` command uploads on request. That is an operator
 *    pulling evidence deliberately, which is the right trust level for a line
 *    that names an asset id.
 * 2. **A day-bucketed tally by code**, shaped deliberately like
 *    `PlaybackTally` in `src/tv/lib/playbackTelemetry.ts` — same day roll, same
 *    "counts, never a trail" rule, same `null`-when-nothing-happened contract.
 *
 * ⚠️ The tally does NOT ride the heartbeat today, and that is a wire-shape fact
 * rather than a decision: `sanitizeTally` in `src/server/api/screens.ts`
 * rebuilds the report field by field from `PlaybackTallyReport`
 * (`src/shared/types/foyer.ts`), so an extra field on the client object is
 * dropped in transit. Widening it is a shared-type + server change; until then
 * {@link currentTrackTally} is a drop-in for exactly that slot and the warning
 * is the outlet that actually reaches a person.
 *
 * ## Why the asset id is in the log line, and why that is not a viewing record
 *
 * `playbackTelemetry` records what the player SAW and never what was watched.
 * This is a data-quality finding about a **catalogue row**, and an unnamed one
 * is unactionable — "some title somewhere promises Finnish subtitles" cannot be
 * fixed by anybody. So the id goes in, the *finding* is what is reported, and
 * the line only exists at all when the two sources genuinely contradict each
 * other, which is rare by construction. Nothing is recorded for a title that
 * plays exactly what it advertised.
 */

export interface TrackTally {
  /** Disagreements seen today, by named code. */
  disagreements: Partial<Record<TrackDisagreement, number>>;
  /** Local date these counts belong to (YYYY-MM-DD). */
  day: string;
}

function today(now: Date): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate()
  ).padStart(2, "0")}`;
}

function emptyTally(day: string): TrackTally {
  return { disagreements: {}, day };
}

let tally: TrackTally = emptyTally(today(new Date()));

function current(now = new Date()): TrackTally {
  const day = today(now);
  if (tally.day !== day) tally = emptyTally(day);
  return tally;
}

/**
 * Warned-once keys, so a title that re-adapts twenty times during a film does
 * not spend the 500-line log ring on twenty copies of one finding. Bounded, so
 * a long-running screen cannot grow this without limit.
 */
const warned = new Set<string>();
const WARNED_MAX = 200;

/** Test seam, and what a role change away from playback should call. */
export function resetTrackReport(now = new Date()): void {
  tally = emptyTally(today(now));
  warned.clear();
}

/**
 * Report every disagreement between a title's labels and its manifest.
 *
 * Safe to call on every `trackschanged` — repeats are counted once per
 * (asset, code) and warned once. Never throws: a reporting path that can take
 * playback down with it is worse than the fault it reports.
 */
export function reportTrackDisagreements(
  disagreements: readonly LanguageDisagreement[],
  assetId?: string | null,
  now?: Date
): void {
  if (disagreements.length === 0) return;
  const t = current(now);
  const id = (assetId ?? "").trim() || "unidentified-title";
  const fresh: LanguageDisagreement[] = [];
  for (const d of disagreements) {
    const key = `${id}|${d.code}|${d.languages.join(",")}`;
    if (warned.has(key)) continue;
    // Evict the oldest rather than stop recording. Refusing to add past the cap
    // would INVERT the guarantee: the key would never be remembered, so the
    // same finding would warn and count on every `trackschanged` - unbounded
    // spam through a 500-line ring, on exactly the long-running screen the cap
    // was added to protect. A Set iterates in insertion order, so the first key
    // is the oldest.
    if (warned.size >= WARNED_MAX) {
      const oldest = warned.values().next().value;
      if (oldest !== undefined) warned.delete(oldest);
    }
    warned.add(key);
    fresh.push(d);
    t.disagreements[d.code] = (t.disagreements[d.code] ?? 0) + 1;
  }
  if (fresh.length === 0) return;
  try {
    // One line, one prefix, greppable in an uploaded log. The prefix is the
    // contract - `log_capture` output is read with grep, not with an eye.
    console.warn(
      `[player] track_labels_disagree title=${id} ${fresh
        .map(describeDisagreement)
        .join(" ")}`
    );
  } catch {
    /* a console that throws must not stop a film */
  }
}

/**
 * Today's counts, or null when nothing disagreed.
 *
 * Null rather than a zeroed block, for the same reason `currentPlaybackTally`
 * answers null: "this screen found nothing wrong" and "this screen is not a
 * playback surface" are different answers, and a wall of zeroes across a
 * hotel's door signs would bury the one room that matters.
 */
export function currentTrackTally(now?: Date): TrackTally | null {
  const t = current(now);
  return Object.keys(t.disagreements).length > 0
    ? { day: t.day, disagreements: { ...t.disagreements } }
    : null;
}
